import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ArticleCollectionType,
  CategoryCollectionType,
  StrapiQueryOptions,
  StrapiWebserviceError,
  getAllParentsCategoriesQueryParams,
  getArticleByAuthorQueryParams,
  getArticlesByCategorySlugQueryParams,
  getArticlesQueryParams,
  getAuthorByIdQueryParams,
  getAuthorBySlugQueryParams,
  getCategoriesQueryParams,
  isCustomerExclusivityActivated,
} from '@wizbii/utils';
import {
  AuthorResponse,
  BlogTypeEnum,
  CategoryCollection,
  CmsComponentEnum,
  LangEnum,
  NativeCtaResponse,
} from '@wizbii/utils/models';
import { Observable, catchError, throwError } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class StrapiWebservice {
  readonly #httpClient = inject(HttpClient);

  readonly #handleError = (error: HttpErrorResponse, message: string) => {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(`Backend returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message.
    return throwError(
      () =>
        new StrapiWebserviceError(message, {
          status: error.status,
          url: error.url as string | undefined,
          originalError: error.statusText,
        })
    );
  };

  readonly getArticles = <B extends BlogTypeEnum, L extends LangEnum>(
    lang: L,
    blogType: B,
    strapiUrl: string,
    options: StrapiQueryOptions,
    obendyPartnerId?: string
  ): Observable<ArticleCollectionType<B>[B]> => {
    const query = getArticlesQueryParams(
      lang,
      options,
      isCustomerExclusivityActivated(blogType) ? obendyPartnerId : undefined
    );
    const resourceRoute = blogType === BlogTypeEnum.Articles ? `articles` : `${blogType}-articles`;
    return this.#httpClient
      .get<ArticleCollectionType<B>[B]>(`${strapiUrl}/${resourceRoute}?${query}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while fetching articles`)));
  };

  readonly getArticlesByCategorySlug = <B extends BlogTypeEnum, L extends LangEnum>(
    lang: L,
    blogType: B,
    strapiUrl: string,
    categorySlugs: string[],
    options: Omit<StrapiQueryOptions, 'slugs'>,
    obendyPartnerId?: string
  ): Observable<ArticleCollectionType<B>[B]> => {
    const query = getArticlesByCategorySlugQueryParams(
      lang,
      blogType,
      categorySlugs,
      options,
      isCustomerExclusivityActivated(blogType) ? obendyPartnerId : undefined
    );
    const resourceRoute = blogType === BlogTypeEnum.Articles ? `articles` : `${blogType}-articles`;
    return this.#httpClient
      .get<ArticleCollectionType<B>[B]>(`${strapiUrl}/${resourceRoute}?${query}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while fetching articles by category slug`)));
  };

  readonly getCategories = <B extends BlogTypeEnum>(
    lang: LangEnum,
    blogType: B,
    strapiUrl: string,
    options: StrapiQueryOptions
  ): Observable<CategoryCollectionType[B]> => {
    const query = getCategoriesQueryParams(lang, options);
    const resourceRoute = blogType === BlogTypeEnum.Articles ? `categories` : `${blogType}-categories`;
    return this.#httpClient
      .get<CategoryCollectionType[B]>(`${strapiUrl}/${resourceRoute}?${query}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while fetching categories`)));
  };

  readonly getAllParentsCategories = (
    lang: LangEnum,
    blogType: BlogTypeEnum,
    strapiUrl: string,
    deepness?: number
  ): Observable<CategoryCollection> => {
    const query = getAllParentsCategoriesQueryParams(lang, deepness);
    const resourceRoute = blogType === BlogTypeEnum.Articles ? `categories` : `${blogType}-categories`;
    return this.#httpClient
      .get<CategoryCollection>(`${strapiUrl}/${resourceRoute}?${query}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while fetching parent categories`)));
  };

  readonly getNativeCta = (
    strapiUrl: string,
    component: CmsComponentEnum.Category | CmsComponentEnum.Article,
    id?: string
  ): Observable<NativeCtaResponse> => {
    if (!id) {
      throw new Error(`id is required`);
    }

    return this.#httpClient
      .get<NativeCtaResponse>(`${strapiUrl}/media-cta/random/${component}/${id}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while fetching native cta`)));
  };

  readonly getAuthorById = (
    lang: LangEnum,
    id: string,
    strapiUrl: string,
    options: StrapiQueryOptions
  ): Observable<AuthorResponse> => {
    const query = getAuthorByIdQueryParams(lang, options);
    return this.#httpClient
      .get<AuthorResponse>(`${strapiUrl}/authors/${id}?${query}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while getting author`)));
  };

  readonly getAuthorBySlug = (
    lang: LangEnum,
    slug: string,
    strapiUrl: string,
    options: StrapiQueryOptions
  ): Observable<AuthorResponse> => {
    const query = getAuthorBySlugQueryParams(lang, slug, options);
    return this.#httpClient
      .get<AuthorResponse>(`${strapiUrl}/authors?${query}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while getting author`)));
  };

  readonly getArticlesByAuthor = <B extends BlogTypeEnum>(
    lang: LangEnum,
    blogType: B,
    strapiUrl: string,
    options: { id?: string } & StrapiQueryOptions,
    obendyPartnerId?: string
  ): Observable<ArticleCollectionType<B>[B]> => {
    const query = getArticleByAuthorQueryParams(
      lang,
      options,
      isCustomerExclusivityActivated(blogType) ? obendyPartnerId : undefined
    );
    const resourceRoute = blogType === BlogTypeEnum.Articles ? `articles` : `${blogType}-articles`;
    return this.#httpClient
      .get<ArticleCollectionType<B>[B]>(`${strapiUrl}/${resourceRoute}?${query}`)
      .pipe(catchError((e) => this.#handleError(e, `An error occurred while fetching articles`)));
  };
}
